// networkState.ts
import { atom, type Getter, type Setter } from 'jotai';
import { store } from '..';
import { logger } from '../../utils/logger';

// Define the network status atom with a default value
export const networkStatusAtom = atom<boolean>(true);

// The app host serves this file in every build (see set-build-time in vite.config).
export const BUILD_TIME_PATH = '/build-time.json';

// A HEAD probe of the app host's build manifest, resolving true only on a 2xx.
// The host is Cloudflare Pages, not the API, so the probe asks for a file that
// really exists rather than lean on the SPA fallback that answers 200 with
// index.html for unknown paths. HEAD plus no-cache bypasses the (GET-only)
// service worker, so a cached copy cannot fake a reply. Rejections and aborts
// propagate to the caller.
export const probeAppHost = async (signal: AbortSignal): Promise<boolean> => {
  const response = await fetch(BUILD_TIME_PATH, {
    method: 'HEAD',
    cache: 'no-cache',
    signal,
  });
  return response.ok;
};

// "Online" means the Insite API answers, not just that some host does: going down
// the online path while the API is unreachable can sign the inspector out (AU-7).
// The liveness endpoint is anonymous and skips the database check, so polling it
// from every device adds no database load. GET is what the deploy health checks
// already prove reaches the API end to end. No service-worker route matches this
// URL, so the service worker never answers it.
const probeApi = async (signal: AbortSignal): Promise<boolean> => {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/health/live`, {
    method: 'GET',
    cache: 'no-store',
    signal,
  });
  return response.ok;
};

// Track consecutive failures for exponential backoff
let consecutiveFailures = 0;
let lastCheckTime = 0;
let isChecking = false;
let latestCheck: Promise<boolean> | null = null;

// Function to check network status
export const checkNetworkStatus = async (): Promise<boolean> => {
  // A caller during a check, or within the 2-second throttle after one, gets that
  // check's result. The atom's default `true` would otherwise answer for the first
  // check before it finishes and send an offline launch down the online path.
  if (latestCheck && (isChecking || Date.now() - lastCheckTime < 2000)) {
    return latestCheck;
  }

  isChecking = true;
  lastCheckTime = Date.now();
  latestCheck = runNetworkCheck().finally(() => {
    isChecking = false;
  });
  return latestCheck;
};

const runNetworkCheck = async (): Promise<boolean> => {
  try {
    // First check browser's online status
    if (!navigator.onLine) {
      consecutiveFailures++;
      if (consecutiveFailures === 1) {
        console.log(
          '🔴 NETWORK TEST: Browser reports OFFLINE (navigator.onLine = false)',
        );
        logger.debug('Browser reports offline status');
      }
      return false;
    }

    // No fallback probes: a public host answering says nothing about the API, and
    // when unsure the app should read offline (a false offline only delays a sync).
    let connected = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    try {
      connected = await probeApi(controller.signal);
    } catch {
      // Unreachable, blocked by CORS, or timed out - reported as offline below.
    } finally {
      clearTimeout(timeoutId);
    }

    if (connected) {
      // Network is back - reset failure counter
      if (consecutiveFailures > 0) {
        console.log(
          '🟢 NETWORK TEST: Connection RESTORED! Back online after',
          consecutiveFailures,
          'failed checks',
        );
        logger.info('Network connectivity restored');
        consecutiveFailures = 0;
      }
      return true;
    } else {
      throw new Error('API health check failed');
    }
  } catch (error) {
    consecutiveFailures++;

    // Only log significant network events, not every failed check
    if (consecutiveFailures === 1) {
      console.log(
        '🟡 NETWORK TEST: First connectivity check failed - monitoring...',
      );
      logger.debug(
        'Network connectivity check failed - monitoring connection...',
      );
    } else if (consecutiveFailures === 5) {
      console.log(
        '🟠 NETWORK TEST: 5 consecutive failures - network likely down',
      );
    } else if (consecutiveFailures === 10) {
      console.log(
        '🔴 NETWORK TEST: 10 failures - definitely offline, backing off checks',
      );
      logger.warn(
        'Network appears to be offline - will continue checking in background',
      );
    }

    return false;
  }
};

// Initialize network monitoring
let timeoutId: ReturnType<typeof setTimeout>;
let networkChangeListener: (() => void) | null = null;

const checkStatus = async (get: Getter, set: Setter) => {
  const status = await checkNetworkStatus();
  const previousStatus = get(networkStatusAtom);

  // Only update and log if status actually changed
  if (status !== previousStatus) {
    set(networkStatusAtom, status);

    if (status) {
      logger.info('🟢 Network status: ONLINE');
    } else {
      logger.warn('🔴 Network status: OFFLINE');
    }
  }

  // Exponential backoff for offline checks (max 30 seconds)
  let interval: number;
  if (status) {
    interval = 30000; // 30s when online (reduced from 10s)
  } else {
    // Exponential backoff: 3s, 6s, 12s, 24s, 30s (max)
    const baseInterval = 3000;
    const backoffMultiplier = Math.min(
      Math.pow(2, Math.floor(consecutiveFailures / 3)),
      10,
    );
    interval = Math.min(baseInterval * backoffMultiplier, 30000);
    console.log(
      `⏱️ NETWORK TEST: Next check in ${interval / 1000} seconds (backoff due to ${consecutiveFailures} failures)`,
    );
  }

  timeoutId = setTimeout(() => checkStatus(get, set), interval);
};

// Function to start monitoring
export const startNetworkMonitoring = () => {
  logger.debug('Starting network monitoring...');

  // Start the monitoring using the store
  checkStatus(store.get, store.set);

  // Listen for network change events
  if (typeof window !== 'undefined') {
    // Handle online/offline events
    networkChangeListener = () => {
      logger.info('Network change event detected');
      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Check network status immediately
      checkStatus(store.get, store.set);
    };

    window.addEventListener('online', networkChangeListener);
    window.addEventListener('offline', networkChangeListener);

    // Also listen for connection type changes if available
    if (
      'connection' in navigator &&
      'addEventListener' in (navigator as any).connection
    ) {
      (navigator as any).connection.addEventListener(
        'change',
        networkChangeListener,
      );
    }
  }

  // Return cleanup function
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (networkChangeListener && typeof window !== 'undefined') {
      window.removeEventListener('online', networkChangeListener);
      window.removeEventListener('offline', networkChangeListener);
      if (
        'connection' in navigator &&
        'removeEventListener' in (navigator as any).connection
      ) {
        (navigator as any).connection.removeEventListener(
          'change',
          networkChangeListener,
        );
      }
    }
    networkChangeListener = null;
    logger.debug('Stopped network monitoring');
  };
};
